// packages/shared/src/schemas/order.ts
import { z } from 'zod'
import { optionalEmail } from './optional-email.js'

export const OrderStatusEnum = z.enum([
  'pending',
  'confirmed',
  'processing',
  'shipped',
  'delivered',
  'cancelled',
])

export const PaymentMethodEnum = z.enum(['cod', 'bank_transfer', 'upi', 'razorpay'])
export const PaymentStatusEnum = z.enum(['pending', 'paid', 'failed', 'refunded'])

export const OrderItemSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  productId: z.string(),
  variantId: z.string().nullable(),
  productName: z.string(),
  variantDescription: z.string().nullable(),
  unitPrice: z.number().min(0),
  quantity: z.number().int().min(1),
  lineTotal: z.number().min(0),
})

export const OrderSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  customerId: z.string().nullable().optional(),
  customerPhone: z.string(),
  customerName: z.string(),
  customerEmail: z.string().email().nullable(),
  status: OrderStatusEnum,

  shippingAddressLine1: z.string(),
  shippingAddressLine2: z.string().nullable(),
  shippingCity: z.string(),
  shippingState: z.string(),
  shippingPincode: z.string(),
  shippingLandmark: z.string().nullable(),

  subtotal: z.number().min(0),
  discountAmount: z.number().min(0),
  shippingAmount: z.number().min(0),
  taxCgst: z.number().min(0),
  taxSgst: z.number().min(0),
  total: z.number().min(0),

  paymentMethod: PaymentMethodEnum,
  paymentStatus: PaymentStatusEnum,
  paymentReference: z.string().nullable(),

  trackingId: z.string(),
  notes: z.string().nullable(),
  adminNotes: z.string().nullable(),
  quoteId: z.string().nullable(),

  items: z.array(OrderItemSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const CheckoutItemInput = z.object({
  productId: z.string().optional(),
  variantId: z.string().optional(),
  comboOfferId: z.string().optional(),
  quantity: z.number().int().min(1).max(99999),
}).refine(
  (d) => Boolean(d.comboOfferId) || Boolean(d.productId),
  { message: 'Each cart line needs a product or a combo offer' },
)

export const CreateOrderInput = z.object({
  customerPhone: z.string()
    .regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit Indian mobile number'),
  customerName: z.string().min(1, 'Name is required').max(100).trim(),
  customerEmail: optionalEmail,

  shippingAddressLine1: z.string().min(5, 'Address required').max(200).trim(),
  shippingAddressLine2: z.string().max(200).trim().optional(),
  shippingCity: z.string().min(1, 'City required').max(100).trim(),
  shippingState: z.string().min(1).max(100).trim().default('Tamil Nadu'),
  shippingPincode: z.string()
    .regex(/^\d{6}$/, 'Enter a valid 6-digit pincode'),
  shippingLandmark: z.string().max(200).trim().optional(),

  paymentMethod: PaymentMethodEnum.default('cod'),
  notes: z.string().max(500).trim().optional(),
  items: z.array(CheckoutItemInput).min(1, 'Cart cannot be empty'),
})

export const UpdateOrderStatusInput = z.object({
  status: OrderStatusEnum,
  message: z.string().min(1).max(500),
  adminNotes: z.string().max(1000).optional(),
})

export const OrderListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().trim().optional(),
  status: OrderStatusEnum.optional(),
  paymentStatus: PaymentStatusEnum.optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  trackingId: z.string().trim().optional(),
  city: z.string().trim().optional(),
  customerPhone: z.string().trim().optional(),
  customerId: z.string().optional(),
  sortBy: z.enum(['created_at', 'total', 'order_number']).default('created_at'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
})

export type Order = z.infer<typeof OrderSchema>
export type CreateOrderInput = z.infer<typeof CreateOrderInput>
export type UpdateOrderStatusInput = z.infer<typeof UpdateOrderStatusInput>
